import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Center, Loader } from "@mantine/core";
import { ReportPreviewModal } from "../../components/workflows/ReportPreviewModal";
import { setInternalRenderToken } from "../../lib/internalRenderAuth";
import { setRenderMode } from "../../lib/renderModeFlag";
import {
  clearInternalRenderBootstrapToken,
  getInternalRenderBootstrapToken,
} from "../../lib/internalRenderBootstrap";
import {
  clearInternalRenderFailure,
  signalInternalRenderFailure,
} from "../../lib/internalRenderSentinel";
import type { ReportRenderContext } from "../../types/reportRenderContext";
import { hydrateReportRenderMapAssets } from "../../services/reportRenderMapAssets.service";
import {
  fetchReportRenderContext,
  matchesReportRenderSubject,
} from "../../services/legacyReconstructionRender.service";

/**
 * Internal report and reconstruction routes share the proper frontend capture
 * renderer. Each uses a short-lived fragment token bound to its report subject;
 * reconstruction contexts supply every input from their frozen snapshot.
 *
 * After all required data and image bytes are ready, the
 * `<ReportPreviewModal renderForCapture>` flips
 * `<html data-render-complete="true">` and Puppeteer reads `page.content()`.
 */
export default function RenderReportPage() {
  const { signOffId, reconstructionId } = useParams<{
    signOffId: string;
    reconstructionId: string;
  }>();
  const [token] = useState(getInternalRenderBootstrapToken);
  const [renderAttached] = useState(
    () =>
      new URLSearchParams(window.location.search).get("part") ===
      "attached-report"
  );
  const handleClose = useCallback(() => undefined, []);

  const [context, setContext] = useState<ReportRenderContext | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const failRender = () => {
      if (!active) return;
      setError(signalInternalRenderFailure());
    };
    const cleanup = () => {
      active = false;
      setInternalRenderToken(null);
      clearInternalRenderFailure();
    };

    if (
      !token ||
      (!signOffId && !reconstructionId) ||
      (reconstructionId && renderAttached)
    ) {
      failRender();
      return cleanup;
    }

    if (reconstructionId) setRenderMode(true);
    else setInternalRenderToken(token);
    clearInternalRenderBootstrapToken();

    fetchReportRenderContext({ signOffId, reconstructionId }, token)
      .then(async (ctx) => {
        if (!matchesReportRenderSubject(ctx, { signOffId, reconstructionId })) {
          failRender();
          return;
        }
        if (!active) return;
        const attached = ctx.reportRenderSnapshot?.reportData?.attachedReport;
        if (renderAttached && !attached) {
          failRender();
          return;
        }
        const selectedContext =
          renderAttached && attached
            ? {
                ...ctx,
                hasExternalBaseline: false,
                reportRenderSnapshot: attached,
              }
            : ctx;
        const hydratedContext = await hydrateReportRenderMapAssets(
          selectedContext
        );
        if (active) setContext(hydratedContext);
      })
      .catch(failRender);

    return cleanup;
  }, [token, signOffId, reconstructionId, renderAttached]);

  if (error) {
    return (
      <Center h="100vh">
        <Alert color="red" title="Report rendering unavailable" role="alert">
          {error}
        </Alert>
      </Center>
    );
  }

  if (!context) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }

  return (
    <ReportPreviewModal
      opened={false}
      onClose={handleClose}
      caseId={context.caseId}
      renderForCapture
      reportVersion={context.reportVersion}
      forceTemplateId={context.templateId || undefined}
      diagnosisCategoriesOverride={context.diagnosisCategories}
      hasExternalBaseline={context.hasExternalBaseline}
      documentViewDataOverride={context.reportRenderSnapshot?.reportData}
      previewDataOverride={
        context.reportRenderSnapshot?.supplementalData?.previewData
      }
      pathologistCommentsOverride={
        context.reportRenderSnapshot?.supplementalData?.pathologistComments
      }
      reportAttachmentsOverride={
        context.reportRenderSnapshot?.supplementalData?.reportComposition
          ? context.reportRenderSnapshot.supplementalData
              .cumulativeReportAttachments ||
            context.reportRenderSnapshot.supplementalData.reportAttachments
          : context.reportRenderSnapshot?.supplementalData?.reportAttachments
      }
      reportCompositionOverride={
        context.reportRenderSnapshot?.supplementalData?.reportComposition
      }
    />
  );
}
